import { FC, useCallback, useMemo, useState } from "react";
import "./BoardFastFilters.css";
import PriorityButton from "../../../assets/priorityButton.svg";
import ArrowDown from "../../../assets/arrowDown.svg";
import TypeButton from "../../../assets/typeButton.svg";
import TagFilter from "../../../assets/tag.svg";
import Search from "../../UiLib/Search/Search";
import { FilterOptionsExtented, IIdNameStyle, IIdNameStyleIcon } from "../../../models/FilterModels/IFilterModel";
import { ISelectedFastFilters } from "../../../contexts/BoardContext";
import { ITag } from "../../../common/api_interfaces/tag/ITag";

interface IBoardFastFiltersProps {
    taskFilters: FilterOptionsExtented;
    changeFastFilters: (filters: ISelectedFastFilters) => void;
    selectedFastFilters: ISelectedFastFilters;
}

const BoardFastFilters: FC<IBoardFastFiltersProps> = ({ taskFilters, changeFastFilters, selectedFastFilters }) => {
    const [activeDropdown, setActiveDropdown] = useState<"priorities" | "types" | "tags" | null>(null);
    const [searchText, setSearchText] = useState<string>("");

    const handlePriorityBtnClick = useCallback(() => {
        setActiveDropdown(prev => prev === "priorities" ? null : "priorities");
    }, []);

    const handleTypeBtnClick = useCallback(() => {
        setActiveDropdown(prev => prev === "types" ? null : "types");
    }, []);

    const handleTagBtnClick = useCallback(() => {
        setActiveDropdown(prev => prev === "tags" ? null : "tags");
    }, []);

    const closeAllDropdowns = useCallback(() => {
        setActiveDropdown(null);
    }, []);

    const tagsForView = useMemo(() => {
        if (!taskFilters.tag) return [];
        const searchTextLowercase = searchText.toLowerCase();
        return taskFilters.tag.filter(t => t.name.toLowerCase().includes(searchTextLowercase));
    }, [searchText, taskFilters.tag]);

    const handleSelectFilter = useCallback((filter: IIdNameStyle | IIdNameStyleIcon | ITag, filterType: "priority" | "type" | "tag") => {
        const isSameFilter = selectedFastFilters[filterType]?.id === filter.id;
        const isResetFilter = filter.id === 0;

        const newFilters = {
            priority: filterType === "priority" ? 
                (isResetFilter ? undefined : filter as IIdNameStyle) : 
                selectedFastFilters.priority,
            type: filterType === "type" ? 
                (isResetFilter ? undefined : filter as IIdNameStyleIcon) : 
                selectedFastFilters.type,
            tag: filterType === "tag" ? 
                (isResetFilter ? undefined : filter as ITag) : 
                selectedFastFilters.tag,
        };

        if (isSameFilter || isResetFilter) {
            changeFastFilters(newFilters);
        } else {
            changeFastFilters(newFilters);
        }
    }, [selectedFastFilters, changeFastFilters]);

    const handleSelectType = useCallback((type: IIdNameStyleIcon) => {
        handleSelectFilter(type, "type");
    }, [handleSelectFilter]);

    const handleSelectPriority = useCallback((priority: IIdNameStyle) => {
        handleSelectFilter(priority, "priority");
    }, [handleSelectFilter]);

    const handleSelectTag = useCallback((tag: ITag) => {
        handleSelectFilter(tag, "tag");
        setSearchText("");
    }, [handleSelectFilter]);

    const handleSearchChange = useCallback((text: string) => {
        setSearchText(text);
    }, []);

    const renderDropdown = useCallback((type: "priorities" | "types" | "tags") => {
        if (activeDropdown !== type) return null;

        const getItems = () => {
            switch (type) {
                case "priorities":
                    return taskFilters.priority?.map(priority => (
                        <div
                            key={priority.id}
                            onClick={() => handleSelectPriority(priority)}
                            className={`board-filter-dropdown__item ${
                                selectedFastFilters.priority?.id === priority.id ? "selected" : ""
                            }`}
                        >
                            {priority.name}
                        </div>
                    ));
                case "types":
                    return taskFilters.typeTask?.map(typeItem => (
                        <div
                            key={typeItem.id}
                            onClick={() => handleSelectType(typeItem)}
                            className={`board-filter-dropdown__item ${
                                selectedFastFilters.type?.id === typeItem.id ? "selected" : ""
                            }`}
                        >
                            {typeItem.name}
                        </div>
                    ));
                case "tags":
                    return (
                        <>
                            <div className="board-filter-dropdown__tag--search">
                                <Search
                                    onSubmitFunc={undefined}
                                    value={searchText}
                                    changeSearchValue={handleSearchChange}
                                />
                            </div>
                            <div className="board-filter-dropdown__tag--search__block--teg">
                                {tagsForView.map(tag => (
                                    <div
                                        key={tag.id}
                                        onClick={() => handleSelectTag(tag)}
                                        className={`board-filter-dropdown__item ${
                                            selectedFastFilters.tag?.id === tag.id ? "selected__board-filter-dropdown__item" : ""
                                        }`}
                                        style={tag.color ? { backgroundColor: tag.color } : {}}
                                    >
                                        {tag.name}
                                    </div>
                                ))}
                            </div>
                        </>
                    );
                default:
                    return null;
            }
        };

        const resetHandler = () => {
            switch (type) {
                case "priorities":
                    handleSelectPriority({ id: 0, name: "Все", style: "" });
                    break;
                case "types":
                    handleSelectType({ id: 0, name: "Все", style: "" });
                    break;
                case "tags":
                    handleSelectTag({ id: 0, name: "Все", color: "" });
                    setSearchText("");
                    break;
            }
        };

        return (
            <>
                <div className="fast-filters-popup-overlay" onClick={closeAllDropdowns}></div>
                <div 
                    className={`board-filter-dropdown ${type === "tags" ? "board-filter-dropdown__tag" : ""}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {getItems()}
                    <div
                        onClick={resetHandler}
                        className="board-filter-dropdown__item--del"
                    >
                        сбросить
                    </div>
                </div>
            </>
        );
    }, [
        activeDropdown, taskFilters, selectedFastFilters, searchText, 
        tagsForView, handleSelectPriority, handleSelectType, handleSelectTag, 
        handleSearchChange, closeAllDropdowns
    ]);

    const priorityButtonText = useMemo(() => 
        selectedFastFilters.priority?.name || "Приоритет", 
        [selectedFastFilters.priority]
    );

    const typeButtonText = useMemo(() => 
        selectedFastFilters.type?.name || "Тип", 
        [selectedFastFilters.type]
    );

    const tagButtonText = useMemo(() => 
        selectedFastFilters.tag?.name || "Теги", 
        [selectedFastFilters.tag]
    );

    return (
        <div className="board-fast-filters-container">
            <div className="board-filter-container">
                <button className="board-filter-container__priority" onClick={handlePriorityBtnClick}>
                    <img src={PriorityButton} alt="Приоритет" />
                    {priorityButtonText}
                    <img src={ArrowDown} alt="▼" />
                </button>
                {renderDropdown("priorities")}
            </div>
            {taskFilters.typeTask && (
                <div className="board-filter-container">
                    <button className="board-filter-container__type" onClick={handleTypeBtnClick}>
                        <img src={TypeButton} alt="Тип" />
                        {typeButtonText}
                        <img src={ArrowDown} alt="▼" />
                    </button>
                    {renderDropdown("types")}
                </div>
            )}
            {taskFilters.tag && (
                <div className="board-filter-container">
                    <button className="board-filter-container__type" onClick={handleTagBtnClick}>
                        <img src={TagFilter} width="14px" alt="Теги" />
                        {tagButtonText}
                        <img src={ArrowDown} alt="▼" />
                    </button>
                    {renderDropdown("tags")}
                </div>
            )}
        </div>
    );
};

export default BoardFastFilters;